import { Injectable, inject } from '@angular/core';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { PlatformService } from '@services/platform.service';
import {
  GalleryMediaKind,
  SaveMediaToGalleryRequest,
  SaveMediaToGalleryResult,
  SaveMediaToGalleryRuntimeOptions,
} from '@services/media-gallery.interfaces';

interface AlbumsPathResponse {
  path: string;
}

interface MediaSaveOptions {
  path: string;
  albumIdentifier?: string;
  fileName?: string;
}

interface MediaPlugin {
  getAlbumsPath(): Promise<AlbumsPathResponse>;
  createAlbum(options: { name: string }): Promise<void>;
  savePhoto(options: MediaSaveOptions): Promise<void>;
  saveVideo(options: MediaSaveOptions): Promise<void>;
}

interface ResolvedFileName {
  fullName: string;
  baseName: string;
}

class DownloadCancelledError extends Error {
  public constructor() {
    super('Download cancelled');
    this.name = 'DownloadCancelledError';
  }
}

const mediaPlugin = registerPlugin<MediaPlugin>('Media');

@Injectable({
  providedIn: 'root'
})
export class MediaGalleryService {
  private readonly platformService = inject(PlatformService);

  private readonly androidAlbumName: string = 'Pyjam';
  private readonly photoExtensions: Set<string> = new Set<string>([
    'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'avif', 'bmp', 'tif', 'tiff', 'svg'
  ]);
  private readonly videoExtensions: Set<string> = new Set<string>([
    'mp4', 'mov', 'm4v', 'webm', 'mkv', 'avi', '3gp', '3gpp', 'mpeg', 'mpg', 'ogv'
  ]);

  public async saveRemoteFileToGallery(
    request: SaveMediaToGalleryRequest,
    options?: SaveMediaToGalleryRuntimeOptions,
  ): Promise<SaveMediaToGalleryResult> {
    if (!this.platformService.isDevice) {
      return 'failed';
    }

    if (!Capacitor.isPluginAvailable('Media')) {
      console.warn('[MediaGallery] Media plugin is not available on this build');
      return 'failed';
    }

    if (options?.signal?.aborted === true) {
      return 'cancelled';
    }

    const sourceUrl: string = request.url.trim();
    if (!sourceUrl) {
      return 'failed';
    }

    const mediaKind: GalleryMediaKind | null = this.resolveMediaKind(
      request.mediaKind ?? null,
      sourceUrl,
      request.fileName ?? null
    );
    if (!mediaKind) {
      return 'failed';
    }

    const albumIdentifier: string | undefined = await this.resolveAlbumIdentifier();
    if (this.platformService.isAndroidApp && !albumIdentifier) {
      return 'failed';
    }

    const resolvedName: ResolvedFileName = this.resolveFileName(
      sourceUrl,
      request.fileName ?? null,
      mediaKind
    );

    const operationId: string = this.createOperationId();
    const cachePath: string = `${operationId}-${resolvedName.fullName}`;
    let cacheFileWritten: boolean = false;

    try {
      const blob: Blob = await this.downloadBlobWithProgress(sourceUrl, options);
      const base64Data: string = await this.blobToBase64(blob, options?.signal);
      await Filesystem.writeFile({
        path: cachePath,
        data: base64Data,
        directory: Directory.Cache,
        recursive: true,
      });
      cacheFileWritten = true;

      if (options?.signal?.aborted) {
        throw new DownloadCancelledError();
      }

      const fileUri = await Filesystem.getUri({
        path: cachePath,
        directory: Directory.Cache,
      });

      await this.saveToGallery(mediaKind, fileUri.uri, resolvedName.baseName, albumIdentifier);
      return 'saved';
    } catch (error) {
      if (this.isCancelledError(error)) {
        return 'cancelled';
      }

      if (this.isPermissionDeniedError(error)) {
        return 'permission-denied';
      }

      console.error('[MediaGallery] save via cache failed, trying direct save', {
        sourceUrl,
        mediaKind,
        error,
      });

      try {
        if (options?.signal?.aborted) {
          return 'cancelled';
        }

        await this.saveToGallery(mediaKind, sourceUrl, resolvedName.baseName, albumIdentifier);
        return 'saved';
      } catch (fallbackError) {
        if (this.isCancelledError(fallbackError)) {
          return 'cancelled';
        }

        if (this.isPermissionDeniedError(fallbackError)) {
          return 'permission-denied';
        }

        console.error('[MediaGallery] direct save failed', {
          sourceUrl,
          mediaKind,
          fallbackError,
        });
        return 'failed';
      }
    } finally {
      if (cacheFileWritten) {
        await this.cleanupCacheFile(cachePath);
      }
    }
  }

  private async downloadBlobWithProgress(
    sourceUrl: string,
    options?: SaveMediaToGalleryRuntimeOptions,
  ): Promise<Blob> {
    if (options?.signal?.aborted === true) {
      throw new DownloadCancelledError();
    }

    return new Promise<Blob>((resolve, reject): void => {
      const xhr = new XMLHttpRequest();
      const signal = options?.signal;
      let settled = false;

      const cleanup = (): void => {
        xhr.onprogress = null;
        xhr.onload = null;
        xhr.onerror = null;
        xhr.onabort = null;
        if (signal) {
          signal.removeEventListener('abort', abortListener);
        }
      };

      const finishWithError = (error: Error): void => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        reject(error);
      };

      const finishWithBlob = (blob: Blob): void => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        resolve(blob);
      };

      const abortListener = (): void => {
        try {
          xhr.abort();
        } catch {
          finishWithError(new DownloadCancelledError());
        }
      };

      xhr.open('GET', sourceUrl, true);
      xhr.responseType = 'blob';

      xhr.onprogress = (event: ProgressEvent<EventTarget>): void => {
        if (!options?.onProgress) {
          return;
        }
        const loadedBytes: number = typeof event.loaded === 'number' && event.loaded > 0
          ? Math.round(event.loaded)
          : 0;
        const totalBytes: number = typeof event.total === 'number' && event.total > 0
          ? Math.round(event.total)
          : 0;
        const percent: number = totalBytes > 0
          ? Math.max(0, Math.min(100, Math.round((loadedBytes / totalBytes) * 100)))
          : 0;

        options.onProgress({
          percent,
          loadedBytes,
          totalBytes,
        });
      };

      xhr.onload = (): void => {
        if (xhr.status < 200 || xhr.status >= 300) {
          finishWithError(new Error(`Download failed with status ${xhr.status}`));
          return;
        }

        const response = xhr.response;
        if (!(response instanceof Blob)) {
          finishWithError(new Error('Invalid downloaded payload'));
          return;
        }

        if (options?.onProgress) {
          const totalBytes: number = response.size > 0 ? response.size : 0;
          options.onProgress({
            percent: 100,
            loadedBytes: totalBytes,
            totalBytes,
          });
        }

        finishWithBlob(response);
      };

      xhr.onerror = (): void => {
        finishWithError(new Error('Download failed'));
      };

      xhr.onabort = (): void => {
        finishWithError(new DownloadCancelledError());
      };

      if (signal) {
        signal.addEventListener('abort', abortListener, { once: true });
      }

      try {
        xhr.send();
      } catch (error) {
        if (error instanceof Error) {
          finishWithError(error);
          return;
        }
        finishWithError(new Error('Download failed'));
      }
    });
  }

  private async blobToBase64(blob: Blob, signal?: AbortSignal): Promise<string> {
    if (signal?.aborted === true) {
      throw new DownloadCancelledError();
    }

    return new Promise<string>((resolve, reject): void => {
      const reader = new FileReader();
      let settled = false;

      const cleanup = (): void => {
        reader.onerror = null;
        reader.onload = null;
        if (signal) {
          signal.removeEventListener('abort', abortListener);
        }
      };

      const rejectWith = (error: Error): void => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        reject(error);
      };

      const resolveWith = (value: string): void => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        resolve(value);
      };

      const abortListener = (): void => {
        try {
          reader.abort();
        } catch {
          rejectWith(new DownloadCancelledError());
        }
      };

      reader.onerror = (): void => {
        rejectWith(new Error('FileReader failed'));
      };

      reader.onload = (): void => {
        const result = reader.result;
        if (typeof result !== 'string') {
          rejectWith(new Error('Invalid FileReader result'));
          return;
        }
        const parts: string[] = result.split(',');
        const base64 = parts.length > 1 ? parts[1] : result;
        resolveWith(base64);
      };

      if (signal) {
        signal.addEventListener('abort', abortListener, { once: true });
      }

      try {
        reader.readAsDataURL(blob);
      } catch {
        rejectWith(new Error('FileReader failed'));
      }
    });
  }

  private isCancelledError(error: unknown): boolean {
    if (error instanceof DownloadCancelledError) {
      return true;
    }

    if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
      return true;
    }

    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
      const normalized = error.message.toLowerCase();
      return normalized.includes('cancel') || normalized.includes('abort');
    }

    return false;
  }

  private isPermissionDeniedError(error: unknown): boolean {
    const message: string = this.resolveErrorMessage(error).toLowerCase();
    const code: string = this.resolveErrorCode(error).toLowerCase();

    if (code.includes('access') && code.includes('denied')) {
      return true;
    }

    if (code.includes('permission') && code.includes('denied')) {
      return true;
    }

    if (message.includes('access to photos') || message.includes('photo library')) {
      return true;
    }

    if (message.includes('not allowed by user')) {
      return true;
    }

    return message.includes('permission')
      && message.includes('denied')
      && (message.includes('photo') || message.includes('gallery') || message.includes('media'));
  }

  private async cleanupCacheFile(path: string): Promise<void> {
    try {
      await Filesystem.deleteFile({
        path,
        directory: Directory.Cache,
      });
    } catch {
      // Ignore cleanup failures: file may be absent if download failed early.
    }
  }

  private resolveMediaKind(
    explicitKind: GalleryMediaKind | null,
    sourceUrl: string,
    fileName: string | null,
  ): GalleryMediaKind | null {
    if (explicitKind) {
      return explicitKind;
    }

    const extension: string | null = this.resolveExtension(sourceUrl, fileName);
    if (!extension) {
      return null;
    }

    if (this.videoExtensions.has(extension)) {
      return 'video';
    }

    if (this.photoExtensions.has(extension)) {
      return 'photo';
    }

    return null;
  }

  private resolveExtension(sourceUrl: string, fileName: string | null): string | null {
    const candidate: string = this.pickPreferredName(sourceUrl, fileName);
    const normalized: string = this.stripQueryAndHash(candidate).trim().toLowerCase();
    if (!normalized) {
      return null;
    }

    const dotIndex: number = normalized.lastIndexOf('.');
    if (dotIndex < 0 || dotIndex === normalized.length - 1) {
      return null;
    }

    return normalized.slice(dotIndex + 1);
  }

  private resolveFileName(sourceUrl: string, fileName: string | null, mediaKind: GalleryMediaKind): ResolvedFileName {
    const preferredName: string = this.pickPreferredName(sourceUrl, fileName).trim();
    const fallbackExtension: string = mediaKind === 'video' ? 'mp4' : 'jpg';
    const fallbackName: string = `file-${Date.now()}.${fallbackExtension}`;
    const sanitizedName: string = this.sanitizeFileName(preferredName || fallbackName);
    const ensuredName: string = this.ensureFileExtension(sanitizedName, mediaKind);
    const dotIndex: number = ensuredName.lastIndexOf('.');
    const baseName: string = dotIndex > 0
      ? ensuredName.slice(0, dotIndex)
      : ensuredName;

    return {
      fullName: ensuredName,
      baseName: baseName || `file-${Date.now()}`,
    };
  }

  private pickPreferredName(sourceUrl: string, fileName: string | null): string {
    const normalizedFileName: string = typeof fileName === 'string' ? fileName.trim() : '';
    if (normalizedFileName) {
      return normalizedFileName;
    }

    const cleanUrl: string = this.stripQueryAndHash(sourceUrl);
    const slashIndex: number = cleanUrl.lastIndexOf('/');
    const encodedName: string = slashIndex >= 0 ? cleanUrl.slice(slashIndex + 1) : cleanUrl;
    if (!encodedName) {
      return '';
    }

    try {
      return decodeURIComponent(encodedName).trim();
    } catch {
      return encodedName.trim();
    }
  }

  private stripQueryAndHash(value: string): string {
    const queryIndex: number = value.indexOf('?');
    const hashIndex: number = value.indexOf('#');
    const firstCutIndex: number = queryIndex >= 0 && hashIndex >= 0
      ? Math.min(queryIndex, hashIndex)
      : Math.max(queryIndex, hashIndex);

    return firstCutIndex >= 0 ? value.slice(0, firstCutIndex) : value;
  }

  private sanitizeFileName(name: string): string {
    const trimmed: string = name.trim();
    const safe: string = trimmed.replace(/[\\/:*?"<>|]/g, '_');
    return safe.replace(/\s+/g, ' ');
  }

  private ensureFileExtension(fileName: string, mediaKind: GalleryMediaKind): string {
    const extension: string | null = this.resolveExtension(fileName, null);
    if (extension) {
      return fileName;
    }

    const fallbackExtension: string = mediaKind === 'video' ? 'mp4' : 'jpg';
    return `${fileName}.${fallbackExtension}`;
  }

  private async resolveAlbumIdentifier(): Promise<string | undefined> {
    if (!this.platformService.isAndroidApp) {
      return undefined;
    }

    let albumsPathResponse: AlbumsPathResponse;
    try {
      albumsPathResponse = await mediaPlugin.getAlbumsPath();
    } catch (error) {
      console.error('[MediaGallery] getAlbumsPath failed', { error });
      return undefined;
    }

    const basePath: string = typeof albumsPathResponse?.path === 'string' ? albumsPathResponse.path.trim() : '';
    if (!basePath) {
      return undefined;
    }

    try {
      await mediaPlugin.createAlbum({ name: this.androidAlbumName });
    } catch (error) {
      const message: string = this.resolveErrorMessage(error);
      const alreadyExists: boolean = message.toLowerCase().includes('already exists');
      if (!alreadyExists) {
        console.warn('[MediaGallery] createAlbum failed', { error });
      }
    }

    return `${basePath}/${this.androidAlbumName}`;
  }

  private async saveToGallery(
    mediaKind: GalleryMediaKind,
    inputPath: string,
    baseName: string,
    albumIdentifier: string | undefined,
  ): Promise<void> {
    const saveOptions: MediaSaveOptions = {
      path: inputPath,
      fileName: baseName,
    };

    if (albumIdentifier) {
      saveOptions.albumIdentifier = albumIdentifier;
    }

    if (mediaKind === 'video') {
      await mediaPlugin.saveVideo(saveOptions);
      return;
    }

    await mediaPlugin.savePhoto(saveOptions);
  }

  private resolveErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
      return error.message;
    }

    if (error && typeof error === 'object' && 'errorMessage' in error && typeof error.errorMessage === 'string') {
      return error.errorMessage;
    }

    return '';
  }

  private resolveErrorCode(error: unknown): string {
    if (error && typeof error === 'object' && 'code' in error && typeof error.code === 'string') {
      return error.code;
    }

    return '';
  }

  private createOperationId(): string {
    const randomPart: string = Math.random().toString(36).slice(2, 10);
    return `${Date.now()}-${randomPart}`;
  }
}
